import { useField } from '@tanstack/react-form'
import debug from 'debug'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Button from 'src/components/buttons/button'
import Card from 'src/components/containers/card'
import { VendiaFormApi } from 'src/components/fields/form'
import ListboxField from 'src/components/fields/listbox.field'
import TextField from 'src/components/fields/text.field'
import TextAreaField from 'src/components/fields/text-area.field'
import Popover from 'src/components/messages/popover'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import { CancelButton, NextButton, PreviousButton } from 'src/components/flows/step-buttons'
import { StepButtonsWrapper } from 'src/components/flows/step-buttons-wrapper'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import Loader from 'src/components/loaders/page-loader'
import { useDevSettings } from 'src/pages/developer-settings.page'
import { AWS_REGIONS, AZURE_REGIONS } from 'src/utils/csp/regions'
import useGetCurrentVendiaUserQuery from 'src/utils/hooks/use-current-vendia-user-query'
import useListUnis from 'src/utils/hooks/use-list-unis'

import { UniCreateFormValues } from './standard.flow'

const logger = debug('app:add-nodes')

export const NODE_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9-]{0,1023}$/

// TODO: Select closest region by default with Geolocation API?
// One option is to match against https://github.com/turnkeylinux/aws-datacenters/blob/a7106906e60969e917e555fd770454f72c78bb75/output/countries.index
// Alternatively, deploy and invoke a Lambda@Edge and get the region from that, or use https://api.cloudping.co/averages

export const DEFAULT_CSP = 'aws'
const AVAILABLE_CSPS = [
  { value: 'aws', label: 'AWS' },
  { value: 'azure', label: 'Azure (Preview)' },
]

export const DEFAULT_REGION: Record<string, string> = {
  aws: 'us-west-2',
  azure: 'westus2',
}

const AVAILABLE_REGIONS: Record<string, any> = {
  aws: AWS_REGIONS,
  azure: AZURE_REGIONS,
}

function getNodeNameFromWorkspace(workspace: string, existingNodes: any[] = []) {
  // Get base name from workspace, replace underscaces/spaces with dashes, remove other special chars
  const baseName = workspace
    ?.trim()
    .replaceAll(/[_\s]/g, '-')
    .replaceAll(/[^a-zA-Z0-9-]/g, '')

  if (!baseName) return 'default-workspace'

  // Check if this exact name already exists
  const existingNames = existingNodes.map((node) => node.name).filter(Boolean)

  if (!existingNames.includes(baseName)) {
    return baseName
  }

  // Find next available suffix
  let suffix = 2
  let candidateName = `${baseName}-${suffix.toString().padStart(2, '0')}`

  while (existingNames.includes(candidateName)) {
    suffix++
    candidateName = `${baseName}-${suffix.toString().padStart(2, '0')}`
  }

  return candidateName
}

export const StepAddNodes: StepComponent<UniCreateFormValues> = ({ context }) => {
  const form = context.form
  const navigate = useNavigate()
  const uniJoinMode = form.getFieldValue('uniJoinMode')

  return (
    <StepWrapper>
      <StepAddNodesContent uniJoinMode={uniJoinMode} form={form} />
      <form.Subscribe selector={(state) => [state.isSubmitting]}>
        {([isSubmitting]) => (
          <StepButtonsWrapper>
            <div className='flex gap-4'>
              <PreviousButton disabled={isSubmitting} onClick={context.goPrevious} />
              <CancelButton onClick={() => navigate('/')} />
            </div>
            <NextButton
              label={isSubmitting ? 'Creating Project...' : 'Finish'}
              className='min-w-[112px]' // Let it get wider when text updates to "Creating Project..."
              onClick={form.handleSubmit}
              disabled={isSubmitting}
              isSubmitting={isSubmitting}
            />
          </StepButtonsWrapper>
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}

type StepAddNodesContentProps = {
  uniJoinMode?: boolean
  form: VendiaFormApi<any>
}
// Used here and by Uni join page
export const StepAddNodesContent = ({ uniJoinMode = false, form }: StepAddNodesContentProps) => {
  const { id: uniName } = useParams()

  const { state, pushValue, removeValue } = useField({ name: 'nodes', form, mode: 'array' })

  const { listUnisQuery } = useListUnis()
  const { data: unis } = listUnisQuery
  const { getCurrentVendiaUserQuery } = useGetCurrentVendiaUserQuery()
  const userId = getCurrentVendiaUserQuery?.data?.getUser?.userId

  const { devSettings } = useDevSettings()
  const devSettingsDefaultRegion = devSettings?.defaultNodeRegion || DEFAULT_REGION[DEFAULT_CSP]

  // State for showing the tooltip message when workspace changes
  const [tooltipState, setTooltipState] = useState<{ show: boolean; nodeIndex: number; message: string }>({
    show: false,
    nodeIndex: -1,
    message: '',
  })

  const handleCloudProviderChange = (provider: string, nodeIndex: number) => {
    /* Set default value if provider changed */
    const defaultRegion = DEFAULT_REGION[provider]
    logger(`Setting default region to ${defaultRegion} for node ${nodeIndex}`)
    // Let new options render first, then select the default
    setTimeout(() => {
      form.setFieldValue(`nodes.${nodeIndex}.region`, defaultRegion)
    }, 0)
  }

  const handleWorkspaceChange = (newRegion: string, nodeIndex: number) => {
    // Get all other nodes (excluding current one) for duplicate checking
    const otherNodes = state.value?.filter((_: any, i: number) => i !== nodeIndex) || []

    // Generate new name from workspace, checking for duplicates
    const newNodeName = getNodeNameFromWorkspace(newRegion, otherNodes)

    // Check existing name - if it matches the new name, do nothing
    const existingName = form.getFieldValue(`nodes.${nodeIndex}.name`)
    if (existingName === newNodeName) {
      return
    }

    // Update the node name field
    form.setFieldValue(`nodes.${nodeIndex}.name`, newNodeName)

    // Show tooltip message near the name field
    setTooltipState({
      show: true,
      nodeIndex,
      message: `Workspace name updated to match selected region: "${newNodeName}"`,
    })

    // Auto-hide the tooltip after 3 seconds
    setTimeout(() => {
      setTooltipState((prev) => ({ ...prev, show: false }))
    }, 3000)
  }

  function addNode(name = '', region = '') {
    const defaultRegion = (region || devSettingsDefaultRegion) ?? DEFAULT_REGION[DEFAULT_CSP]
    const nodeName = name || getNodeNameFromWorkspace(defaultRegion, state.value || [])

    pushValue({
      name: nodeName,
      csp: DEFAULT_CSP,
      region: defaultRegion,
      description: '',
    })
  }
  // Minor hack - we add a node on first render - checking fields.length
  // prevents rendering any content on first render
  //  so user doesn't see [ + Add Node ] button jumping around
  useEffect(() => {
    if (!state.value || state.value?.length === 0) {
      addNode()
    }
  }, [devSettingsDefaultRegion])

  if (!unis || !userId) {
    return (
      <ScrollableStepContent inset>
        <Loader />
      </ScrollableStepContent>
    )
  }

  return (
    <>
      {uniJoinMode ? (
        <StepContentHeader
          title={`Join the ${uniName} project`}
          centered={true}
          large={true}
          hasMarginY={true}
          description={`You've been invited to join the ${uniName} project! Adjust the configuration below to add a workspace in your region of choice and then click the Accept invite and add workspace button.`}
        />
      ) : (
        <StepContentHeader
          title={'Configure a project workspace'}
          className='max-w-4xl'
          centered={true}
          large={true}
          description={
            <p>
              Review the settings for your project's workspace below. Additional workspaces can be added after project
              creation, and each workspace can be owned by a different organization. Please note that a workspace's
              settings cannot be changed later.
            </p>
          }
        />
      )}
      <ScrollableStepContent inset autoHeight>
        <div className='w-full max-w-4xl space-y-8'>
          {state.value?.length > 0 && (
            <>
              {state.value.map((node: any, index: number) => {
                return (
                  <Card padding='xl' key={index}>
                    <Popover
                      open={tooltipState.show && tooltipState.nodeIndex === index}
                      content={<div className='p-3 text-sm font-bold'>{tooltipState.message}</div>}
                      side='top'
                      triggerAsChild
                    >
                      <div className='flex flex-col gap-4'>
                        <ListboxField
                          name={`nodes.${index}.csp`}
                          defaultValue={node.csp}
                          label='Cloud Service Provider'
                          description={'The cloud environment used to host your workspace on Vendia.'}
                          form={form}
                          data-testid={'node-csp-select'}
                          options={AVAILABLE_CSPS}
                          validators={{
                            onChange: ({ value }: any) => {
                              if (!value) {
                                return 'This field is required.'
                              }
                              if (value === 'azure') {
                                return 'Azure Regions are not currently supported for your account plan. Please contact support@vendia.com to enroll in the public preview for Azure.'
                              }
                            },
                          }}
                          onChange={(value) => handleCloudProviderChange(value!, index)}
                        />
                        <ListboxField
                          name={`nodes.${index}.region`}
                          defaultValue={node.region}
                          label='Workspace location'
                          description='Select the AWS region where your workspace will be deployed.'
                          form={form}
                          data-testid={'node-region-select'}
                          options={AVAILABLE_REGIONS[form.state.values.nodes[index].csp || DEFAULT_CSP].map(
                            ({ value, label }: { value: string; label: string }) => ({
                              label: `${value} (${label})`,
                              value: value,
                            }),
                          )}
                          onChange={(value) => handleWorkspaceChange(value!, index)}
                        />
                        <TextField
                          name={`nodes.${index}.name`}
                          defaultValue={node.name}
                          label='Workspace name'
                          description={'Enter a unique workspace name without spaces or special characters.'}
                          placeholder='my-new-workspace'
                          form={form}
                          data-testid='node-name-input'
                          validators={{
                            onBlur: ({ value }: any) => {
                              if (!value) {
                                return 'This field is required.'
                              }
                              if (!NODE_NAME_PATTERN.test(value)) {
                                return 'Name may only include numbers, letters, and dashes, and must start with a letter.'
                              }

                              const nodesWithCurrentValueAsName = state.value
                                ?.map((node: any) => node?.name)
                                .filter((name: string) => name === value)

                              if (nodesWithCurrentValueAsName?.length > 1) {
                                return 'Node name must be unique.'
                              }
                            },
                          }}
                        />
                        <TextAreaField
                          name={`nodes.${index}.description`}
                          defaultValue={node.description}
                          label='Workspace description'
                          description='Optional workspace description.'
                          placeholder='Description of my-new-workspace...'
                          maxLength={1000}
                          form={form}
                          data-testid='node-description-input'
                          validators={{
                            onBlur: ({ value }: any) => {
                              if (value?.length > 1000) {
                                return 'Description must be 1000 characters or less.'
                              }
                            },
                          }}
                        />
                      </div>
                    </Popover>
                    {form.state.values.nodes.length > 1 && (
                      <div className={'mt-4'}>
                        <Button kind='secondary' icon='trash' onClick={() => removeValue(index)}>
                          Remove workspace
                        </Button>
                      </div>
                    )}
                  </Card>
                )
              })}
              {!uniJoinMode && (
                <div className='text-sm'>
                  <Button
                    kind={'anchor'}
                    onClick={(e) => {
                      e.preventDefault()
                      addNode()
                    }}
                  >
                    Add another workspace owned by your organization (this is uncommon)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </ScrollableStepContent>
    </>
  )
}
